import { useEffect, useMemo, useState } from "react";
import { useReturnRequestApi } from "../../hooks/useReturnRequestApi.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import { ReturnRequest, returnRequestFields } from "./types.ts";
import { DataGrid, GridActionsCellItem, GridColDef, GridDeleteIcon } from "@mui/x-data-grid";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ConfirmDialog from "../estimates/ConfirmDialog.tsx";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField } from "@mui/material";
import { Button } from "reactstrap";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import dayjs from "dayjs";
import { styles } from "../../contracts/estimateStyle.tsx";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableRow } from "../../layouts/SortableRow.tsx";
import { ItemsEditor } from "../estimates/ItemEditor.tsx";

type Row = {
  id: string;
  companyName: string;
  returnDate: string;
  personInCharge: string;
  mobileInCharge: string;
  createdTime: string;
  raw: ReturnRequest;
};

interface ReturnRequestListProbs{
    data: ReturnRequest[];
    isLoading: boolean;
    onRefresh?: () => Promise<void> | void;
}

export default function ReturnRequestTable({
    data,
    isLoading,
    onRefresh
}: ReturnRequestListProbs) {

    const { deleteReturnRequest } = useReturnRequestApi()

    const rows: Row[] = useMemo(
            () =>
              data.map((e) => ({
                id: e.id,
                companyName: e.companyName,
                returnDate: e.returnTime,
                personInCharge: e.personInCharge,
                mobileInCharge: e.mobileInCharge,
                createdTime: e.createTime,
                raw: e
              })),
            [data]
          );

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [targetId, setTargetId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [expandedId, setExpandedId] = useState<string | null>(null);
    
    const askDelete = (id: string) => {
        setTargetId(id);
        setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        if (!targetId) return;
        try {
        setSubmitting(true);
        await deleteReturnRequest(targetId);
        setConfirmOpen(false);
        setTargetId(null);
        if (onRefresh) await onRefresh();
        else window.location.reload();
        } catch (e: any) {
        alert(e.message ?? "삭제 중 오류");
        } finally {
        setSubmitting(false);
        }
    };

    const [detailOpen, setDetailOpen] = useState(false);
    const [editing, setEditing] = useState<ReturnRequest | null>(null);

    const openDetail = (dto: ReturnRequest) => { setEditing(dto); setDetailOpen(true); };
    const closeDetail = () => { setDetailOpen(false); setEditing(null); };
    
    const columns: GridColDef[] = [
        { field: "companyName", headerName: "회사명", flex: 1, }, 
        { field: "returnDate", headerName: "회수일자", flex: 1, }, 
        { field: "personInCharge", headerName: "담당자", flex: 1, },  
        { field: "mobileInCharge", headerName: "담당자 연락처", flex: 1, },
        {
            field: "actions",
            type: "actions",
            flex: 1,
            getActions: (params) => [
                <GridActionsCellItem
                    key="items"
                    icon={<ExpandMoreIcon />}
                    label="항목 편집"
                    onClick={() => openDetail((params.row as Row).raw)}
                />,
                <GridActionsCellItem
                    key="delete"
                    icon={<GridDeleteIcon />}
                    label="Delete"
                    onClick={() => askDelete(String(params.id))}
                />
                ]
        },
    ];

    return (
        <>
            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(r) => r.id}
                loading={isLoading && rows.length === 0}
                autoHeight
                pageSizeOptions={[20]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 20, page: 0 } },
                    sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
                }}
                disableRowSelectionOnClick
            />

            <DetailDialog
                open={detailOpen}
                returnRequest={editing}
                onClose={closeDetail}
                onSaved={async () => {
                if (onRefresh) await onRefresh();    // 저장 후 목록 갱신
                }}
            />

            <ConfirmDialog
                open={confirmOpen}
                title="정말 지우시겠습니까?"
                message="이 작업은 되돌릴 수 없습니다."
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
                loading={submitting}
                confirmText="예"
                cancelText="아니오"
            />
        </>
    )

}

function DetailDialog({
  open,
  returnRequest,
  onClose,
  onSaved,
}: {
  open: boolean;
  returnRequest: ReturnRequest | null;
  onClose: () => void;
  onSaved?: (e: ReturnRequest) => void;
}) {
  if (!returnRequest) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      keepMounted
      PaperProps={{ sx: { height: '90vh' } }}     // 미리보기/폼 공간 넉넉히
    >
      <DialogTitle>회수요청서 상세</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>     {/* 내부 패딩 제거 */}
        <DetailForm returnRequest={returnRequest} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailForm({ returnRequest }: { returnRequest: ReturnRequest }) {
  const methods = useForm<ReturnRequest>({ defaultValues: returnRequest });
  const { control, register, handleSubmit, reset, formState } = methods;
  const [saverOpen, setSaverOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const { errors } = formState;
  const {
     updateReturnRequest, downloadPdf
   } = useReturnRequestApi();

  useEffect(() => { if (returnRequest) reset(returnRequest); }, [returnRequest, reset]);

  const askSave = handleSubmit((data) => {
      const payload: ReturnRequest = {
        ...returnRequest,
        modifiedTime: dayjs().tz('Asia/Seoul').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      };
      setSaverOpen(true);
      (window as any).__pendingEstimate__ = payload;
    
  });

  const confirmSave = async () => {
    const pending = (window as any).__pendingEstimate__ as ReturnRequest | undefined;
    if (!pending) return;
    try {
      setSubmitting(true);
      await updateReturnRequest(pending);
      reset(pending);
      setSaverOpen(false);
      (window as any).__pendingEstimate__ = undefined;
      window.location.reload();
    } catch (e: any) {
      alert(e.message ?? '저장 중 오류');
    } finally {
      setSubmitting(false);
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const download = handleSubmit(async (data) => {
    try {
        const payload: ReturnRequest = { ...returnRequest, ...data };
      
        const blob = await downloadPdf(payload);
        setPreviewOpen(true);
      
    } catch (e: any) {
      alert(e?.message ?? '미리보기 실패');
    }
  });

  const sensors = useSensors(
          useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
          );
      const { insertReturnRequest } = useReturnRequestApi();
  
      // remark 배열용
      const {
          fields: remarkList,
          append: appendRemark,
          remove: removeRemark,
          move: moveRemark,
      } = useFieldArray<ReturnRequest, "memo">({
          control,
          name: "memo",
      });
  
      const addRemark = async () => appendRemark("")
  
      // � 드래그 종료 시 배열 순서 업데이트
      const handleDragEndRemark = (event: DragEndEvent) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;
  
          const oldIndex = remarkList.findIndex(f => f.id === active.id);
          const newIndex = remarkList.findIndex(f => f.id === over.id);
          moveRemark(oldIndex, newIndex);          // useFieldArray 의 move
      };

  return (
    <>
      <FormProvider {...methods}>
        <h2 style={styles.largeText}>회수요청서</h2>
          <form style={styles.form}>
            <Stack spacing={4}>
                <div style={styles.grid}>
                <label
                    key={"returnTime"}
                    style={{
                        ...styles.label,
                        ...(false ? styles.fullWidth : null),
                    }}
                    >
                {"회수일시"}
                {true && <span style={styles.required}>*</span>}
                <Controller
                    name="returnTime"
                    control={control}
                    render={({ field }) => (
                        <>
                        <input
                        {...field}
                        type="datetime-local"   // 여기서 날짜+시간 입력 가능
                        style={styles.input}
                        step={60}               // 초 단위 step → 60초 = 1분 단위로 선택
                        />
                        {errors.returnTime && (
                            <span style={{ color: "red" }}>
                            {errors.returnTime.message}
                            </span>
                        )}
                        </>
                    )}
                    />
                </label>

                {returnRequestFields.map(({ label, name, type, required, multiline }) => {
                    // 현재 필드(name)에 해당하는 에러만 꺼내기
                    const fieldError = (errors as any)[name as keyof ReturnRequest];

                    return (
                        <label
                            key={name}
                            style={{
                                ...styles.label,
                                ...(multiline ? styles.fullWidth : null),
                            }}
                        >
                            {label}
                            {required && <span style={styles.required}>*</span>}

                            <Controller
                                name={name as keyof ReturnRequest}
                                control={control}
                                rules={required ? { required: true } : undefined}
                                render={({ field }) => (
                                    <>
                                        <input
                                            {...field}
                                            type={type}
                                            style={styles.input}
                                        />
                                        {fieldError && (
                                            <span style={{ color: "red" }}>
                                                {fieldError.message ?? "필수 입력 항목입니다."}
                                            </span>
                                        )}
                                    </>
                                )}
                            />
                        </label>
                    );
                })}

                </div>
            </Stack>

            <label
                key={"memo"}
                style={{
                    ...styles.label,
                    ...(false ? styles.fullWidth : null),
                }}
                >
                {"메모"}
                
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEndRemark}
                >
                    <SortableContext
                        items={remarkList.map(f => f.id)}          // 반드시 고유 id 배열
                        strategy={verticalListSortingStrategy}
                    >
                    {remarkList.map((item, index) => (
                        <SortableRow key={item.id} id={item.id}>
                            <Stack key={item.id} direction="row" spacing={2} alignItems="flex-start">
                                {/* ── 설명 ── */}
                                <Controller
                                    name={`memo.${index}`}
                                    control={control}
                                    render={({ field }) => (
                                    <TextField {...field} 
                                        label="기타사항"
                                        defaultValue={item[index]}
                                        sx={{ minWidth: 1080 }} />
                                    )}
                                />

                                {/* 삭제 버튼 */}
                                <IconButton onClick={() => removeRemark(index)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        </SortableRow>
                    ))}
                    </SortableContext>
                </DndContext>
                <Button onClick={ addRemark }>
                    + 기타사항 행 추가
                </Button>
            </label>
            
            </form>

        <ItemsEditor />

        <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
          <Button onClick={download}>다운로드</Button>
          <Button onClick={askSave} disabled={!formState.isDirty}>저장</Button>
        </div>
      </FormProvider>

      <ConfirmDialog
        open={saverOpen}
        title="수정하시겠습니까?"
        message="이 작업은 되돌릴 수 없습니다."
        onClose={() => setSaverOpen(false)}
        onConfirm={confirmSave}
        loading={submitting}
        confirmText="예"
        cancelText="아니오"
      />
    </>
  );
}
